/**
 * 3DConnexion SpaceMouse — Utilidades matemáticas para matrices 4x4
 *
 * Funciones mínimas para extraer traslación y rotación de matrices 4x4
 * column-major (el formato que usa el navlib de 3DConnexion).
 *
 * Column-major layout (OpenGL / navlib):
 *   [ m0  m4  m8  m12 ]
 *   [ m1  m5  m9  m13 ]
 *   [ m2  m6  m10 m14 ]
 *   [ m3  m7  m11 m15 ]
 *
 * Traslación = [m12, m13, m14]
 * Columnas 0-2 = rotación (3x3 superior izquierda)
 */

export type Vec3 = [number, number, number];
export type Vec4 = [number, number, number, number];
export type Mat4 = number[]; // 16 elementos, column-major

// ---------------------------------------------------------------------------
// Identidad
// ---------------------------------------------------------------------------

export function identity(): Mat4 {
  return [
    1, 0, 0, 0,
    0, 1, 0, 0,
    0, 0, 1, 0,
    0, 0, 0, 1,
  ];
}

// ---------------------------------------------------------------------------
// Traslación
// ---------------------------------------------------------------------------

/** Extraer el vector de traslación de una matriz 4x4 column-major */
export function getTranslation(m: Mat4): Vec3 {
  return [m[12], m[13], m[14]];
}

/** Calcular el delta de traslación entre dos matrices */
export function deltaTranslation(prev: Mat4, curr: Mat4): Vec3 {
  return [
    curr[12] - prev[12],
    curr[13] - prev[13],
    curr[14] - prev[14],
  ];
}

// ---------------------------------------------------------------------------
// Quaternion desde la parte rotacional de una Mat4
// ---------------------------------------------------------------------------

/**
 * Extraer un quaternion [x, y, z, w] de la sub-matriz de rotación 3x3
 * de una Mat4 column-major.
 * Algoritmo de Shepperd (numéricamente estable).
 */
export function getRotationQuaternion(m: Mat4): Vec4 {
  // Elementos de la sub-matriz 3x3 (column-major):
  // col0 = [m[0], m[1], m[2]]
  // col1 = [m[4], m[5], m[6]]
  // col2 = [m[8], m[9], m[10]]
  const m00 = m[0], m01 = m[4], m02 = m[8];
  const m10 = m[1], m11 = m[5], m12 = m[9];
  const m20 = m[2], m21 = m[6], m22 = m[10];

  const trace = m00 + m11 + m22;
  let x: number, y: number, z: number, w: number;

  if (trace > 0) {
    const s = 0.5 / Math.sqrt(trace + 1.0);
    w = 0.25 / s;
    x = (m21 - m12) * s;
    y = (m02 - m20) * s;
    z = (m10 - m01) * s;
  } else if (m00 > m11 && m00 > m22) {
    const s = 2.0 * Math.sqrt(1.0 + m00 - m11 - m22);
    w = (m21 - m12) / s;
    x = 0.25 * s;
    y = (m01 + m10) / s;
    z = (m02 + m20) / s;
  } else if (m11 > m22) {
    const s = 2.0 * Math.sqrt(1.0 + m11 - m00 - m22);
    w = (m02 - m20) / s;
    x = (m01 + m10) / s;
    y = 0.25 * s;
    z = (m12 + m21) / s;
  } else {
    const s = 2.0 * Math.sqrt(1.0 + m22 - m00 - m11);
    w = (m10 - m01) / s;
    x = (m02 + m20) / s;
    y = (m12 + m21) / s;
    z = 0.25 * s;
  }

  return [x, y, z, w];
}

// ---------------------------------------------------------------------------
// Quaternion helpers
// ---------------------------------------------------------------------------

/** Conjugado (inverso para quaternion unitario) */
function quatConjugate(q: Vec4): Vec4 {
  return [-q[0], -q[1], -q[2], q[3]];
}

/** Multiplicación de quaternions: a * b */
function quatMultiply(a: Vec4, b: Vec4): Vec4 {
  return [
    a[3] * b[0] + a[0] * b[3] + a[1] * b[2] - a[2] * b[1],
    a[3] * b[1] - a[0] * b[2] + a[1] * b[3] + a[2] * b[0],
    a[3] * b[2] + a[0] * b[1] - a[1] * b[0] + a[2] * b[3],
    a[3] * b[3] - a[0] * b[0] - a[1] * b[1] - a[2] * b[2],
  ];
}

// ---------------------------------------------------------------------------
// Euler angles from quaternion
// ---------------------------------------------------------------------------

/**
 * Convertir quaternion [x, y, z, w] a ángulos Euler [rx, ry, rz] en radianes.
 * Convención ZYX (yaw-pitch-roll), que es la más común para navegación.
 */
export function quaternionToEuler(q: Vec4): Vec3 {
  const [x, y, z, w] = q;

  // Roll (X)
  const sinr_cosp = 2.0 * (w * x + y * z);
  const cosr_cosp = 1.0 - 2.0 * (x * x + y * y);
  const rx = Math.atan2(sinr_cosp, cosr_cosp);

  // Pitch (Y) — clamped para evitar NaN en asin
  const sinp = 2.0 * (w * y - z * x);
  const ry = Math.abs(sinp) >= 1
    ? Math.sign(sinp) * (Math.PI / 2)
    : Math.asin(sinp);

  // Yaw (Z)
  const siny_cosp = 2.0 * (w * z + x * y);
  const cosy_cosp = 1.0 - 2.0 * (y * y + z * z);
  const rz = Math.atan2(siny_cosp, cosy_cosp);

  return [rx, ry, rz];
}

// ---------------------------------------------------------------------------
// Delta rotation
// ---------------------------------------------------------------------------

/**
 * Calcular la rotación delta entre dos matrices como ángulos Euler [rx, ry, rz].
 * delta = curr * inverse(prev)
 */
export function deltaRotation(prev: Mat4, curr: Mat4): Vec3 {
  const qPrev = getRotationQuaternion(prev);
  const qCurr = getRotationQuaternion(curr);

  // delta = qCurr * conjugate(qPrev)
  const qDelta = quatMultiply(qCurr, quatConjugate(qPrev));

  return quaternionToEuler(qDelta);
}

// ---------------------------------------------------------------------------
// Utility: crear una cameraMatrix tipo "lookAt" para el viewport virtual
// ---------------------------------------------------------------------------

/**
 * Construir una cameraMatrix sencilla: posición (eye) mirando a target con up=[0,1,0].
 * Retorna una Mat4 column-major.
 */
export function lookAt(eye: Vec3, target: Vec3, up: Vec3 = [0, 1, 0]): Mat4 {
  const zAxis3: Vec3 = normalize(sub(eye, target));
  const xAxis3: Vec3 = normalize(cross(up, zAxis3));
  const yAxis3: Vec3 = cross(zAxis3, xAxis3);

  // Column-major
  return [
    xAxis3[0], yAxis3[0], zAxis3[0], 0,
    xAxis3[1], yAxis3[1], zAxis3[1], 0,
    xAxis3[2], yAxis3[2], zAxis3[2], 0,
    eye[0], eye[1], eye[2], 1,
  ];
}

// Vec3 helpers
function sub(a: Vec3, b: Vec3): Vec3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

function normalize(v: Vec3): Vec3 {
  const len = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
  if (len === 0) return [0, 0, 0];
  return [v[0] / len, v[1] / len, v[2] / len];
}
